import { useEffect, useRef, useState } from "react";
import { portalFetch } from "../api/client";
import type { PortalAcceptanceTrackerDto, PortalSpecCorrectionResult, PortalSpecReadinessDto } from "../model";
import { MessageBody } from "./MessageBody";
import { PortalSelect } from "./PortalSelect";
import { markdownToPlainText, RichText } from "./RichText";

type EvidenceCriterion = PortalAcceptanceTrackerDto["criteria"][number];
type EvidenceForm = NonNullable<EvidenceCriterion["evidenceForm"]>;
type EvidenceValue = string | number | boolean;

function displayEvidenceValue(value: EvidenceValue, unit?: string) {
	if (typeof value === "boolean") return value ? "Yes" : "No";
	return `${value}${unit ? ` ${unit}` : ""}`;
}

const STAGE_COPY = {
	discovery: {
		label: "Spec discovery",
		description:
			"The Project agent is working through the decisions and evidence needed for a buildable specification.",
	},
	adminReview: {
		label: "Waiting for administrator review",
		description:
			"Your current spec work is complete. The administrator now needs to review the changes and either approve delivery or request more discovery.",
	},
	ticketPlanning: {
		label: "Ticket planning",
		description: "The Spec is approved. Delivery remains locked until the ticket plan passes independent review.",
	},
	readyForDelivery: {
		label: "Ready for delivery",
		description: "The specification has passed administrator review and delivery work may begin.",
	},
} as const;

export function SpecProgress({ readiness, compact = false }: { readiness: PortalSpecReadinessDto; compact?: boolean }) {
	const activeSkillReview =
		readiness.consolidation?.protocol === "skillV1" &&
		["queued", "reviewing", "correcting"].includes(readiness.consolidation.status);
	const stage = activeSkillReview
		? {
				label: readiness.consolidation?.phase === "semanticReview" ? "Independent Spec review" : "Writing the Spec",
				description: readiness.consolidation?.summary,
			}
		: readiness.stage === "adminReview" && !readiness.consolidation?.approvalReady
			? { label: "Independent Spec review", description: readiness.consolidation?.summary }
			: STAGE_COPY[readiness.stage];
	return (
		<section className={`spec-progress ${compact ? "is-compact" : ""}`} aria-label="Specification readiness">
			<div className="spec-progress-heading">
				<div>
					<p className="eyebrow">CURRENT STAGE</p>
					<h2>{stage.label}</h2>
				</div>
				<strong>
					{readiness.consolidation?.approvalReady
						? "Review passed"
						: readiness.confidence === null
							? "Review pending"
							: `${readiness.confidence}% confidence`}
				</strong>
			</div>
			{readiness.confidence !== null && (
				<div
					className="spec-confidence-track"
					role="progressbar"
					aria-label="Spec confidence"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={readiness.confidence}
				>
					<span style={{ width: `${readiness.confidence}%` }} />
				</div>
			)}
			{readiness.progress && (
				<p role="status">
					{readiness.progress.decisionsSettled} decisions settled · {readiness.progress.questionsOpen} questions
					open · {readiness.progress.questionsDeferred} deferred · {readiness.progress.researchPending} research
					pending
				</p>
			)}
			<p role="status">{stage.description}</p>
			{readiness.consolidation && !readiness.consolidation.approvalReady && (
				<p>Review status: {readiness.consolidation.status}. Delivery remains locked.</p>
			)}
			{readiness.stage === "ticketPlanning" && <p role="status">{readiness.planningSummary}</p>}
			{readiness.stage === "adminReview" && readiness.consolidation?.approvalReady && (
				<div className="spec-workflow-handoff">
					<span>Ball is with</span>
					<strong>Administrator</strong>
					<small>
						No action is required from you right now. Delivery controls unlock after approval and ticket-plan
						review.
					</small>
				</div>
			)}
			{!compact && readiness.missingAreas.length > 0 && (
				<p className="spec-progress-gaps">
					<strong>
						{readiness.missingAreas.length}{" "}
						{readiness.assessmentCurrent ? "areas still need work" : "areas await current assessment"}:
					</strong>{" "}
					{readiness.missingAreas.slice(0, 3).join(", ")}
					{readiness.missingAreas.length > 3 ? `, and ${readiness.missingAreas.length - 3} more` : ""}.
				</p>
			)}
		</section>
	);
}

export function SpecView({
	projectId,
	mapId,
	specId,
	readiness,
	requirementsContent,
	acceptanceTracker,
	onAcceptanceTrackerUpdated,
	onContinueDiscovery,
	onCorrectionsStarted,
}: {
	projectId: string;
	mapId: string;
	specId: string;
	readiness: PortalSpecReadinessDto;
	requirementsContent: string;
	acceptanceTracker: PortalAcceptanceTrackerDto | null;
	onAcceptanceTrackerUpdated: (tracker: PortalAcceptanceTrackerDto) => void;
	onContinueDiscovery: () => void;
	onCorrectionsStarted: (result: PortalSpecCorrectionResult) => Promise<void>;
}) {
	const [startingCorrections, setStartingCorrections] = useState(false);
	const [correctionError, setCorrectionError] = useState("");
	const correctionActionRef = useRef<HTMLButtonElement>(null);
	const focusCorrectionAction = useRef(false);
	const startCorrections = async () => {
		if (startingCorrections) return;
		setStartingCorrections(true);
		setCorrectionError("");
		try {
			const result = await portalFetch<PortalSpecCorrectionResult>(
				`/api/portal/v1/projects/${projectId}/work-maps/${mapId}/spec-correction`,
				{
					method: "POST",
					body: JSON.stringify({ expectedSpecId: specId }),
				},
			);
			await onCorrectionsStarted(result);
		} catch (reason) {
			setCorrectionError(reason instanceof Error ? reason.message : "The new Spec could not be opened. Try again.");
		} finally {
			setStartingCorrections(false);
		}
	};
	const [recordingKey, setRecordingKey] = useState<string>();
	const [result, setResult] = useState<"passed" | "failed">("passed");
	const [notes, setNotes] = useState("");
	const [fieldValues, setFieldValues] = useState<Record<string, EvidenceValue>>({});
	const [completesCriterion, setCompletesCriterion] = useState(false);
	const [savingEvidence, setSavingEvidence] = useState(false);
	const [evidenceError, setEvidenceError] = useState("");
	useEffect(() => {
		if (!focusCorrectionAction.current || !acceptanceTracker?.correctionAvailable || savingEvidence) return;
		focusCorrectionAction.current = false;
		correctionActionRef.current?.focus({ preventScroll: true });
		correctionActionRef.current?.scrollIntoView({ block: "center" });
	}, [acceptanceTracker?.correctionAvailable, savingEvidence]);
	const resetEvidenceForm = () => {
		setRecordingKey(undefined);
		setResult("passed");
		setNotes("");
		setFieldValues({});
		setCompletesCriterion(false);
		setEvidenceError("");
	};
	const saveEvidence = async (criterionKey: string) => {
		if (savingEvidence) return;
		setSavingEvidence(true);
		setEvidenceError("");
		try {
			const tracker = await portalFetch<PortalAcceptanceTrackerDto>(
				`/api/portal/v1/projects/${projectId}/acceptance-criteria/${encodeURIComponent(criterionKey)}/evidence?mapId=${encodeURIComponent(mapId)}`,
				{
					method: "POST",
					headers: { "Idempotency-Key": crypto.randomUUID() },
					body: JSON.stringify({
						result,
						notes: notes.trim(),
						values: fieldValues,
						completesCriterion: result === "passed" && completesCriterion,
					}),
				},
			);
			focusCorrectionAction.current = result === "failed";
			onAcceptanceTrackerUpdated(tracker);
			resetEvidenceForm();
		} catch (reason) {
			setEvidenceError(reason instanceof Error ? reason.message : "The validation evidence could not be saved.");
		} finally {
			setSavingEvidence(false);
		}
	};
	const formIsReady = (form?: EvidenceForm) => {
		if (!form) return !!notes.trim();
		const requiredFieldsComplete = form.fields.every((field) => {
			if (!field.required) return true;
			const value = fieldValues[field.key];
			return value !== undefined && value !== "";
		});
		const hasFieldValue = form.fields.some((field) => {
			const value = fieldValues[field.key];
			return value !== undefined && value !== "";
		});
		return requiredFieldsComplete && (notes.trim().length > 0 || hasFieldValue);
	};
	return (
		<div className="spec-workspace">
			<SpecProgress readiness={readiness} />
			{acceptanceTracker?.correctionAvailable && (
				<section className="spec-correction-handoff" aria-labelledby="spec-correction-heading">
					<h2 id="spec-correction-heading">Let’s work through what failed</h2>
					<p>
						Your validation is saved. The Project agent can review it with you, ask what is still unclear, and
						start a new Spec for the fixes and checks you need.
					</p>
					<button
						ref={correctionActionRef}
						type="button"
						disabled={startingCorrections || savingEvidence}
						onClick={() => void startCorrections()}
					>
						{startingCorrections ? "Opening new Spec…" : "Start a new Spec"}
					</button>
					<small>The new Spec covers only the new work. Earlier delivery and validation remain recorded.</small>
					{correctionError && <p role="alert">{correctionError}</p>}
				</section>
			)}
			{acceptanceTracker && (
				<section className="acceptance-tracker" aria-labelledby="acceptance-tracker-heading">
					<div className="acceptance-tracker-heading">
						<div>
							<p className="eyebrow">DELIVERY CHECKLIST</p>
							<h2 id="acceptance-tracker-heading">Acceptance criteria</h2>
							<p>
								This checklist comes directly from the approved Spec. Iterations add evidence; they do not
								create new requirements here.
							</p>
						</div>
						<div className="acceptance-tracker-total">
							<strong>{acceptanceTracker.counts.met}</strong>
							<span>of {acceptanceTracker.counts.total} met</span>
						</div>
					</div>
					<div
						className="acceptance-progress-track"
						role="progressbar"
						aria-label="Acceptance criteria complete"
						aria-valuemin={0}
						aria-valuemax={acceptanceTracker.counts.total}
						aria-valuenow={acceptanceTracker.counts.met}
					>
						<span
							style={{
								width: `${acceptanceTracker.counts.total ? (acceptanceTracker.counts.met / acceptanceTracker.counts.total) * 100 : 0}%`,
							}}
						/>
					</div>
					<ol className="acceptance-checklist">
						{acceptanceTracker.criteria.map((criterion) => (
							<li key={criterion.criterionKey} data-status={criterion.status}>
								<input
									type="checkbox"
									checked={criterion.status === "met"}
									disabled
									aria-label={`${criterion.status === "met" ? "Met" : "Not met"}: ${markdownToPlainText(criterion.text)}`}
								/>
								<div>
									<p>
										<RichText content={criterion.text} inline />
									</p>
									<div className="acceptance-criterion-meta">
										<span>{criterion.status === "notAssessed" ? "Not assessed" : criterion.status}</span>
										{criterion.lastCommitRange && <code>{criterion.lastCommitRange}</code>}
									</div>
									{criterion.evidence && (
										<small className="acceptance-agent-evidence">
											<strong>Agent evidence</strong>
											<RichText content={criterion.evidence} inline />
										</small>
									)}
									{criterion.humanEvidence.length > 0 && (
										<div
											className="acceptance-human-evidence"
											role="region"
											aria-label="Human validation evidence"
										>
											<p>Recorded validation</p>
											{criterion.humanEvidence.map((item) => (
												<article key={item.id} data-result={item.result}>
													<div>
														<strong>{item.result === "passed" ? "Passed" : "Did not pass"}</strong>
														{item.formDefinition && <span>{item.formDefinition.title}</span>}
													</div>
													{item.formDefinition && Object.keys(item.values).length > 0 && (
														<dl>
															{item.formDefinition.fields
																.filter((field) => item.values[field.key] !== undefined)
																.map((field) => (
																	<div key={field.key}>
																		<dt>{field.label}</dt>
																		<dd>
																			{displayEvidenceValue(item.values[field.key], field.unit)}
																		</dd>
																	</div>
																))}
														</dl>
													)}
													{item.notes && (
														<p>
															<RichText content={item.notes} inline />
														</p>
													)}
													<small>
														{item.recordedBy} · {new Date(item.recordedAt).toLocaleString()}
														{item.completesCriterion ? " · Recorded as completing this criterion" : ""}
													</small>
												</article>
											))}
										</div>
									)}
									{criterion.nextAction && (
										<small className="acceptance-next-action">
											<strong>
												{criterion.ownerEvidencePending ? "Waiting on you:" : "Still required:"}
											</strong>{" "}
											<RichText content={criterion.nextAction} inline />
										</small>
									)}
									{recordingKey === criterion.criterionKey ? (
										<form
											className="acceptance-evidence-form"
											onSubmit={(event) => {
												event.preventDefault();
												void saveEvidence(criterion.criterionKey);
											}}
										>
											<div className="acceptance-evidence-form-heading">
												<div>
													<p>{criterion.evidenceForm?.title ?? "Record evidence"}</p>
													<small>
														{criterion.evidenceForm?.instructions ??
															"Add the information that proves what happened. It becomes part of the project evidence the model reviews."}
													</small>
												</div>
												<button type="button" className="text-button" onClick={resetEvidenceForm}>
													Cancel
												</button>
											</div>
											{criterion.evidenceForm && (
												<div className="acceptance-evidence-requested-fields">
													{criterion.evidenceForm.fields.map((field) =>
														field.type === "boolean" ? (
															<PortalSelect
																key={field.key}
																label={field.label}
																ariaLabel={field.label}
																value={
																	fieldValues[field.key] === undefined
																		? ""
																		: String(fieldValues[field.key])
																}
																options={[
																	{ value: "", label: "Choose…" },
																	{ value: "true", label: "Yes" },
																	{ value: "false", label: "No" },
																]}
																onChange={(value) =>
																	setFieldValues((current) => ({
																		...current,
																		[field.key]: value === "" ? "" : value === "true",
																	}))
																}
																required={field.required}
															/>
														) : field.type === "select" ? (
															<PortalSelect
																key={field.key}
																className="acceptance-evidence-field is-select"
																label={
																	<>
																		{field.label}
																		{field.unit && <small>{field.unit}</small>}
																	</>
																}
																ariaLabel={field.label}
																value={(fieldValues[field.key] as string | undefined) ?? ""}
																options={[
																	{ value: "", label: "Choose…" },
																	...(field.options?.map((option) => ({
																		value: option,
																		label: option,
																	})) ?? []),
																]}
																onChange={(value) =>
																	setFieldValues((current) => ({ ...current, [field.key]: value }))
																}
																required={field.required}
															/>
														) : (
															<label
																key={field.key}
																className={`acceptance-evidence-field is-${field.type}`}
															>
																<span>
																	{field.label}
																	{field.unit && <small>{field.unit}</small>}
																</span>
																{field.type === "textarea" ? (
																	<textarea
																		value={(fieldValues[field.key] as string | undefined) ?? ""}
																		onChange={(event) =>
																			setFieldValues((current) => ({
																				...current,
																				[field.key]: event.target.value,
																			}))
																		}
																		placeholder={field.placeholder}
																		required={field.required}
																		rows={3}
																	/>
																) : (
																	<input
																		type={field.type}
																		value={
																			(fieldValues[field.key] as string | number | undefined) ?? ""
																		}
																		onChange={(event) =>
																			setFieldValues((current) => ({
																				...current,
																				[field.key]:
																					field.type === "number" && event.target.value !== ""
																						? Number(event.target.value)
																						: event.target.value,
																			}))
																		}
																		placeholder={field.placeholder}
																		required={field.required}
																	/>
																)}
															</label>
														),
													)}
												</div>
											)}
											<div className="acceptance-evidence-fields">
												<PortalSelect
													label="Result"
													value={result}
													options={[
														{ value: "passed", label: "Passed" },
														{ value: "failed", label: "Did not pass" },
													]}
													onChange={(value) => {
														const next = value as typeof result;
														setResult(next);
														if (next === "failed") setCompletesCriterion(false);
													}}
												/>
											</div>
											<label className="acceptance-evidence-notes">
												<span>
													{criterion.evidenceForm ? "Additional context" : "What did you observe?"}
												</span>
												<textarea
													value={notes}
													onChange={(event) => setNotes(event.target.value)}
													maxLength={4000}
													rows={4}
													placeholder="Add any context the requested fields do not capture."
													required={!criterion.evidenceForm}
												/>
											</label>
											{result === "passed" && (
												<label className="acceptance-completes-check">
													<input
														type="checkbox"
														checked={completesCriterion}
														onChange={(event) => setCompletesCriterion(event.target.checked)}
													/>
													<span>
														<strong>This covers everything still required for this criterion.</strong>
														Use this only when the evidence above completes the whole criterion; otherwise
														the model will combine it with other evidence.
													</span>
												</label>
											)}
											{evidenceError && (
												<p className="acceptance-evidence-error" role="alert">
													{evidenceError}
												</p>
											)}
											<button
												type="submit"
												disabled={!formIsReady(criterion.evidenceForm) || savingEvidence}
											>
												{savingEvidence ? "Saving evidence…" : "Save validation evidence"}
											</button>
										</form>
									) : (
										<button
											type="button"
											className="acceptance-record-button"
											onClick={() => {
												resetEvidenceForm();
												setRecordingKey(criterion.criterionKey);
											}}
										>
											Record validation
										</button>
									)}
								</div>
							</li>
						))}
					</ol>
					<small className="acceptance-tracker-date">
						Approved Spec revision from {new Date(acceptanceTracker.acceptedAt).toLocaleDateString()}.
					</small>
				</section>
			)}
			{readiness.consolidation && (
				<section className="spec-rubric" aria-label="Independent Spec review">
					<h2>Independent Spec review</h2>
					<p>{readiness.consolidation.summary}</p>
					{readiness.consolidation.findings?.map((finding) => (
						<p key={finding.key}>
							<strong>{finding.title}</strong> — {finding.detail}
						</p>
					))}
				</section>
			)}
			{(readiness.openQuestions.length > 0 || readiness.mapBlockers.length > 0) && (
				<section className="spec-open-questions">
					<p className="eyebrow">OPEN DECISIONS</p>
					<h2>Questions still preventing a complete spec</h2>
					<ol>
						{readiness.openQuestions.map((question) => (
							<li key={question}>
								<RichText content={question} inline />
							</li>
						))}
						{readiness.mapBlockers.map((blocker) => (
							<li key={blocker}>{blocker}</li>
						))}
					</ol>
				</section>
			)}
			{readiness.adminFeedback && (
				<section className="spec-admin-feedback">
					<strong>Administrator feedback</strong>
					<RichText content={readiness.adminFeedback} className="message-body" />
				</section>
			)}
			<section className="living-spec" aria-labelledby="living-spec-heading">
				<p className="eyebrow">LIVING SPECIFICATION</p>
				<h2 id="living-spec-heading">Current requirements</h2>
				<MessageBody content={requirementsContent} markdown />
			</section>
		</div>
	);
}
